/**
 * `skaile source manifest …` — curated `skaile.manifest.yaml` lifecycle for a
 * source you do NOT own. `init` generates a curated manifest from a discovery
 * run and stores it OUTSIDE the source, under the local store catalog
 * (`~/.skaile/store/manifests/<slug>.yaml`); `edit` opens it in `$EDITOR` and
 * re-validates on save. `source add`/`sync` then apply it as a read overlay
 * (see `source.ts` `refreshManifestCache`).
 *
 * `rebuild` refreshes a curated manifest after an upstream change (merge by
 * default — curation is sacred; `--overwrite` re-scans). `verify` is a
 * read-only integrity check (paths, declared hashes, schema, dep refs).
 * `publish`/`propose`/`yank` remain stubs — they need the store publish
 * endpoint (`POST /sources/<id>/manifest`), which lands in a follow-up.
 *
 * @docLink cli/commands/source#manifest
 */

import { spawnSync } from "node:child_process";
import { existsSync, readFileSync, writeFileSync } from "node:fs";
import * as path from "node:path";
import {
  type AssetEntry,
  decodeSkaileManifest,
  encodeSkaileManifest,
  findWorkspaceRoot,
  parseAssetRef,
  resolveSourcePublisher,
  type SkPublishManifest,
  type SourceEntry,
  sourceCacheKey,
  sourceShortName,
  storeManifestSlug,
  workspaceConfigFilename,
} from "@skaile/workspaces/core";
import { computeDeterministicHash, discoverAssetsInTree } from "@skaile/workspaces/discovery";
import { getStoreManifestsDir, writeCuratedManifest } from "@skaile/workspaces/library";
import type { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import { createFullRegistry } from "../open-registry.ts";
import {
  ensureClone,
  normalizeSourceUrl,
  readProjectSources,
  sourceClonePath,
  sourceMatchesKey,
} from "./source.ts";

const NOT_WIRED_MSG =
  "`skaile source manifest *` is scaffolded but not yet wired. " +
  "Server-side endpoint (`POST /sources/<id>/manifest`) lands in a follow-up PR.";

function notWired(verb: string): never {
  logErr(`${NOT_WIRED_MSG} (verb: source manifest ${verb})`);
  process.exit(2);
}

/**
 * Resolve a `source manifest` argument into a `{ url, slug, dest }` triple.
 *
 * Resolution order:
 *   1. A key matching an already-registered project source (stored URL, cache
 *      key, or short slug).
 *   2. A collision-free **store slug** (`<host>__<owner>__<repo>`) whose
 *      manifest already exists at `~/.skaile/store/manifests/<arg>.yaml`. This
 *      MUST be tried before bare-name expansion: a bare name like `cli` would
 *      otherwise expand (via `normalizeSourceUrl`) to the skaile-ai org and a
 *      DIFFERENT slug than the one `init` wrote (e.g. `larksuite`). The slug is
 *      the exact identifier `init`'s next-step hint prints back.
 *   3. Otherwise a git URL / `owner/repo` / bare name via `normalizeSourceUrl`.
 *
 * The clone dir is the same URL-keyed cache `source add` uses.
 */
export function resolveSourceArg(
  arg: string,
  projectDir: string,
): { url: string; slug: string; dest: string; manifestFile: string } {
  const root = findWorkspaceRoot(projectDir);
  const registered: SourceEntry[] = root
    ? readProjectSources(path.join(root, workspaceConfigFilename()))
    : [];
  const match = registered.find((e) => sourceMatchesKey(e, arg));
  const bySlug = !match ? resolveByStoreSlug(arg) : undefined;
  const url = match ? match.url : (bySlug?.url ?? normalizeSourceUrl(arg));
  const slug = sourceShortName(url); // human-facing label
  const dest = sourceClonePath(sourceCacheKey(url));
  return { url, slug, dest, manifestFile: bySlug?.manifestFile ?? storeManifestPath(url) };
}

/**
 * When `arg` is a bare store slug (`<host>__<owner>__<repo>`) whose curated
 * manifest already exists on disk, recover the source URL from the slug and
 * point at that manifest file directly. Returns `undefined` when the arg isn't
 * a slug or has no manifest yet (fall back to URL normalization).
 */
function resolveByStoreSlug(arg: string): { url: string; manifestFile: string } | undefined {
  // A slug is our own encoding — no path/URL sigils, at least the `<host>__…`
  // separator. Anything URL/owner-repo shaped is not a slug.
  if (!/^[\w.-]+__[\w.-]+/.test(arg) || /[/:\\]/.test(arg)) return undefined;
  const manifestFile = path.join(getStoreManifestsDir(), `${arg}.yaml`);
  if (!existsSync(manifestFile)) return undefined;
  // `<host>__<owner>__<repo>` → `https://<host>/<owner>/<repo>` — good enough to
  // key the clone dir + re-derive the same slug; the manifest file is what edit
  // actually opens.
  const [host, owner, repo] = arg.split("__");
  const url = host && owner && repo ? `https://${host}/${owner}/${repo}` : arg;
  return { url, manifestFile };
}

/**
 * Absolute path to a source's curated store manifest. Keyed by the
 * collision-free {@link storeManifestSlug} (`<host>__<owner>__<repo>`) so
 * `orgA/cli` and `orgB/cli` don't share `cli.yaml`.
 */
function storeManifestPath(url: string): string {
  return path.join(getStoreManifestsDir(), `${storeManifestSlug(url)}.yaml`);
}

/**
 * Directory-name segments that mark a test-fixture tree. `init`/`rebuild` drop
 * any asset whose repo-relative root contains one of these as an exact path
 * SEGMENT. Repos routinely ship fixture "skills" under such dirs (e.g.
 * `scripts/skill-format-check/tests`, `internal/qualitygate/skillscan/testdata/skills/lark-demo`)
 * that are not real, publishable assets.
 */
const TEST_FIXTURE_SEGMENTS = new Set([
  "tests",
  "test",
  "__tests__",
  "testdata",
  "fixtures",
  "__fixtures__",
]);

/**
 * True when an asset's repo-relative `root` sits under a test-fixture directory
 * (see {@link TEST_FIXTURE_SEGMENTS}). Conservative, not repo-specific — finer
 * curation (keeping a genuine fixture asset) stays the user's job via `edit`.
 * `init`/`rebuild`-path only; the general discovery walk (`source add`) is
 * untouched.
 */
function isTestFixtureAsset(relativePath: string): boolean {
  const segments = relativePath.replace(/\\/g, "/").split("/");
  return segments.some((s) => TEST_FIXTURE_SEGMENTS.has(s));
}

/**
 * `source manifest init <src>` — clone (if needed), run discovery, and write a
 * curated `skaile.manifest.yaml` to `~/.skaile/store/manifests/<slug>.yaml`.
 */
async function runInit(
  arg: string,
  opts: { projectDir: string; publisher?: string; dev?: boolean; force?: boolean },
): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { url, dest, manifestFile: target } = resolveSourceArg(arg, projectDir);

  if (existsSync(target) && !opts.force) {
    logWarn(
      `A curated manifest already exists at ${S.dim(target)}. ` +
        "Edit it with `skaile source manifest edit`, refresh it after an upstream " +
        "change with `skaile source manifest rebuild`, or pass --force to regenerate.",
    );
    return;
  }

  const entry: SourceEntry = { url };
  ensureClone(entry);

  const { assets, errors } = discoverCuratableAssets(dest, opts.dev ?? false);

  const publisher = opts.publisher ?? resolveSourcePublisher(dest) ?? sourceShortName(url);

  const written = writeCuratedManifest({
    manifestFile: target,
    publisher,
    assets,
    force: opts.force,
    // Init: every asset inherits the top-level repo `publisher`. Do NOT stamp a
    // per-asset publisher — on the glob path each DiscoveredAsset.publisher is
    // the bare first path segment (`scripts`/`skills`/…), which would clobber
    // the intended org/`--publisher` default with junk per-asset overrides.
    inheritPublisher: true,
  });

  if (!written.wrote) {
    logWarn(
      `Curated manifest already exists at ${S.dim(target)} — not overwritten. ` +
        "Pass --force to regenerate.",
    );
    return;
  }

  logOk(`Wrote curated manifest with ${written.assetCount} assets to ${S.dim(target)}.`);
  // Echo the EXACT <src> the user passed so the printed commands actually run.
  // A bare short name would mis-resolve through `normalizeSourceUrl` (→ the
  // skaile-ai org, wrong slug); the original arg is what `resolveSourceArg`
  // maps back to this same manifest.
  logInfo(
    `Edit it with \`skaile source manifest edit ${arg}\`, then ` +
      `\`skaile source add ${arg}\` to consume it.`,
  );
  if (errors.length > 0) {
    logWarn(`${errors.length} discovery error(s) — inspect the manifest before consuming.`);
  }
}

/**
 * Run discovery over a clone and drop the test-fixture assets (see
 * {@link isTestFixtureAsset}). Shared by `init` and `rebuild` so both paths
 * produce an identical inventory from the same tree.
 */
function discoverCuratableAssets(
  dest: string,
  includeDev: boolean,
): { assets: ReturnType<typeof discoverAssetsInTree>["assets"]; errors: string[] } {
  const result = discoverAssetsInTree(dest, {
    registry: createFullRegistry(),
    includeDev,
  });
  const assets = result.assets.filter((a) => !isTestFixtureAsset(a.relativePath));
  return { assets, errors: result.errors.map((e) => `${e.path}: ${e.error}`) };
}

/**
 * Best-effort `git pull --ff-only` on an existing clone. Never throws: a pull
 * failure (offline, diverged, detached) is warned and the caller continues with
 * the clone as-is.
 */
function pullCloneFfOnly(dest: string): void {
  const res = spawnSync("git", ["-C", dest, "pull", "--ff-only"], { stdio: "pipe" });
  if (res.status !== 0) {
    logWarn(
      "git pull --ff-only failed — continuing with the existing clone " +
        "(it may be behind upstream).",
    );
  }
}

/**
 * Repo-relative `root` path for a manifest asset entry — the merge match key
 * between an existing curated entry and a freshly-discovered asset. `root:`
 * entries use their declared path; a `files:`-only entry (rare for a curated
 * sidecar) falls back to the entry's dirname of the first file, else the name.
 */
function entryRootKey(entry: AssetEntry): string {
  if (entry.root) return entry.root.replace(/\\/g, "/").replace(/\/+$/, "");
  const files = entry.files;
  if (files && files.length > 0) {
    const first = typeof files[0] === "string" ? files[0] : files[0].path;
    const dir = path.posix.dirname(first.replace(/\\/g, "/"));
    return dir === "." ? entry.name : dir;
  }
  return entry.name;
}

/** Repo-relative `root` path a discovered asset maps to (matches init's writer). */
function discoveredRootKey(relativePath: string): string {
  const dir = path.posix.dirname(relativePath.replace(/\\/g, "/"));
  return dir === "." ? path.posix.basename(relativePath.replace(/\\/g, "/")) : dir;
}

/** Human ref for reporting: `kind:name`. */
function entryRef(entry: AssetEntry): string {
  return `${entry.kind}:${entry.name}`;
}

/**
 * `source manifest rebuild <src>` — refresh a curated manifest after the
 * upstream repo changed. MERGE is the default (curation is sacred); `--overwrite`
 * re-scans from scratch with `init --force` semantics.
 *
 * Merge algorithm (match by repo-relative `root` path):
 *   - Existing entries whose root is STILL discovered → KEPT verbatim (all
 *     hand-edits preserved).
 *   - Existing entries whose root is GONE upstream → DROPPED (reported).
 *   - Freshly-discovered assets not already in the manifest → NOT auto-added,
 *     only REPORTED (a curated foreign sidecar must not silently absorb new
 *     upstream skills). An asset the curator deliberately dropped stays dropped
 *     because it is neither in the existing manifest nor re-added here.
 */
async function runRebuild(
  arg: string,
  opts: { projectDir: string; publisher?: string; dev?: boolean; overwrite?: boolean },
): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { url, dest, slug, manifestFile: target } = resolveSourceArg(arg, projectDir);

  if (!existsSync(target)) {
    logErr(
      `No curated manifest for "${slug}" at ${S.dim(target)} — ` +
        "run `skaile source manifest init` first.",
    );
    process.exit(1);
  }

  // Update the clone: clone if absent, else pull --ff-only (best-effort).
  const entry: SourceEntry = { url };
  const cloned = ensureClone(entry);
  if (!cloned) pullCloneFfOnly(dest);

  const { assets: freshAssets, errors } = discoverCuratableAssets(dest, opts.dev ?? false);

  if (opts.overwrite) {
    logWarn(
      "--overwrite: replacing the curated manifest with a full fresh inventory. " +
        "All hand-curation (dropped assets, per-asset edits) is discarded.",
    );
    const publisher = opts.publisher ?? resolveSourcePublisher(dest) ?? sourceShortName(url);
    const written = writeCuratedManifest({
      manifestFile: target,
      publisher,
      assets: freshAssets,
      force: true,
      inheritPublisher: true,
    });
    logOk(
      `Rebuilt curated manifest with ${written.assetCount} assets to ${S.dim(target)} (overwrite).`,
    );
    if (errors.length > 0) {
      logWarn(`${errors.length} discovery error(s) — inspect the manifest before consuming.`);
    }
    return;
  }

  // MERGE (default). `--publisher` only applies to the full re-derive path.
  if (opts.publisher) {
    logWarn(
      "--publisher is ignored in merge mode (the curated publisher is preserved). " +
        "Use --overwrite to re-derive the publisher from a full re-scan.",
    );
  }

  // Read the existing curated manifest and keep it as the base.
  const { manifest: existing } = decodeSkaileManifest(readFileSync(target, "utf-8"));
  const existingEntries = existing.assets ?? [];

  // Index fresh discovery by repo-relative root path.
  const freshByRoot = new Map<string, (typeof freshAssets)[number]>();
  for (const a of freshAssets) freshByRoot.set(discoveredRootKey(a.relativePath), a);

  const kept: AssetEntry[] = [];
  const dropped: string[] = [];
  for (const e of existingEntries) {
    if (freshByRoot.has(entryRootKey(e))) kept.push(e);
    else dropped.push(entryRef(e));
  }

  // Fresh assets not already curated → report as available, do NOT add.
  const existingRoots = new Set(existingEntries.map(entryRootKey));
  const newlyAvailable: string[] = [];
  for (const a of freshAssets) {
    if (!existingRoots.has(discoveredRootKey(a.relativePath))) {
      newlyAvailable.push(`${a.kind}:${a.name}`);
    }
  }

  const changed = dropped.length > 0;
  if (dropped.length > 0) {
    logWarn(`removed (gone upstream): ${dropped.sort().join(", ")}`);
  }
  if (newlyAvailable.length > 0) {
    logInfo(
      `${newlyAvailable.length} new upstream asset(s) available (not added): ` +
        `${newlyAvailable.sort().join(", ")} — add with \`skaile source manifest edit ${arg}\` if wanted`,
    );
  }

  if (!changed) {
    logOk("already up to date");
    if (errors.length > 0) {
      logWarn(`${errors.length} discovery error(s) — inspect the source before re-consuming.`);
    }
    return;
  }

  const merged: SkPublishManifest = { ...existing, assets: kept };
  writeFileSync(target, encodeSkaileManifest(merged), "utf-8");
  logOk(
    `Rebuilt curated manifest: kept ${kept.length}, dropped ${dropped.length} → ${S.dim(target)}.`,
  );
  if (errors.length > 0) {
    logWarn(`${errors.length} discovery error(s) — inspect the source before re-consuming.`);
  }
}

/**
 * `source manifest verify <src>` — READ-ONLY integrity check of a curated
 * manifest against its clone. Writes nothing. Exit 0 when no errors; with
 * `--strict`, any warning also forces a nonzero exit.
 *
 * Per-asset checks:
 *   - `root`/`files` paths still exist upstream (missing → error).
 *   - A declared `sha256` recomputes to the same deterministic hash (mismatch → error).
 *   - Schema decode diagnostics map to errors/warnings.
 *   - Dependency refs parse via `parseAssetRef` (malformed → warning).
 */
async function runVerify(
  arg: string,
  opts: { projectDir: string; strict?: boolean },
): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { url, dest, slug, manifestFile: target } = resolveSourceArg(arg, projectDir);

  if (!existsSync(target)) {
    logErr(
      `No curated manifest for "${slug}" at ${S.dim(target)} — ` +
        "run `skaile source manifest init` first.",
    );
    process.exit(1);
  }

  // Ensure a clone exists to verify paths/hashes against (best-effort; no pull).
  ensureClone({ url });

  const text = readFileSync(target, "utf-8");
  const { manifest, diagnostics } = decodeSkaileManifest(text);

  const errors: string[] = [];
  const warnings: string[] = [];

  // Schema diagnostics from the decoder.
  for (const d of diagnostics) {
    const line = `${d.code}: ${d.message}`;
    if (d.severity === "error") errors.push(line);
    else warnings.push(line);
  }

  const assets = manifest.assets ?? [];
  for (const entry of assets) {
    const ref = entryRef(entry);

    // Path existence: `root` dir and/or each `files[]` path.
    const relPaths: string[] = [];
    if (entry.root) relPaths.push(entry.root);
    if (entry.files) {
      for (const f of entry.files) relPaths.push(typeof f === "string" ? f : f.path);
    }
    if (relPaths.length === 0) relPaths.push(entry.name);
    for (const rel of relPaths) {
      if (!existsSync(path.join(dest, rel))) {
        errors.push(`${ref}: path not found upstream: ${rel}`);
      }
    }

    // Declared sha256 → recompute + compare, but ONLY for granular `files:`
    // entries. Discovery (`discover-manifest.ts`) anchors a `files:`-only entry
    // at the REPO ROOT and hashes the files VERBATIM as repo-relative strings
    // (the path is folded into the digest), so verify must hash the same way:
    // `assetRoot = dest`, files kept repo-relative — NOT rebased onto the root.
    // A `root:` entry cannot be verified this way (discovery hashes only the
    // per-kind filtered set, not the whole-dir walk), and the writer never emits
    // sha256 for `root:` entries, so skip the hash check for them.
    if (entry.sha256) {
      if (entry.files && entry.files.length > 0) {
        const hashFiles = entry.files.map((f) =>
          (typeof f === "string" ? f : f.path).replace(/\\/g, "/"),
        );
        const actual = computeDeterministicHash(dest, hashFiles);
        if (actual !== entry.sha256) {
          errors.push(
            `${ref}: sha256 mismatch (declared ${entry.sha256.slice(0, 12)}…, got ${actual.slice(0, 12)}…)`,
          );
        }
      } else {
        warnings.push(
          `${ref}: declared sha256 on a root:-mode entry is not verifiable (discovery hashes only the per-kind filtered set) — skipped`,
        );
      }
    }

    // Dependency refs parse.
    for (const dep of entry.dependencies ?? []) {
      try {
        parseAssetRef(dep);
      } catch (err) {
        warnings.push(
          `${ref}: malformed dependency "${dep}": ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  }

  for (const w of warnings) logWarn(w);
  for (const e of errors) logErr(e);

  if (errors.length === 0) {
    logOk(`verified ${assets.length} asset(s).`);
  } else {
    logErr(`${errors.length} issue(s).`);
  }

  const failStrict = opts.strict && warnings.length > 0;
  if (errors.length > 0 || failStrict) process.exit(1);
}

/**
 * Split a `$VISUAL`/`$EDITOR` value into `{ command, args }` and append the file
 * as the final arg. The env value is a full command line, not a bare binary —
 * `EDITOR="code --wait"` must spawn `code` with `["--wait", <file>]`, and
 * `EDITOR=code` (which returns before save) is the user's problem, not ours.
 * A whitespace split covers the common editors (`code --wait`, `code -w`,
 * `subl -w`, `vi`); paths with embedded spaces are not supported (rare for an
 * editor binary and out of scope). Falls back to `vi` when unset/empty.
 *
 * @docLink cli/commands/source#manifest-edit
 */
export function resolveEditorCommand(
  editorEnv: string | undefined,
  target: string,
): { command: string; args: string[] } {
  const parts = (editorEnv ?? "").trim().split(/\s+/).filter(Boolean);
  const [command, ...rest] = parts.length > 0 ? parts : ["vi"];
  return { command: command as string, args: [...rest, target] };
}

/** Minimal spawn contract runEdit needs — lets tests inject a fake. */
type SpawnLike = (
  command: string,
  args: string[],
  opts: { stdio: "inherit" },
) => { status: number | null };

/**
 * `source manifest edit <src>` — open the curated store manifest in `$EDITOR`
 * and re-validate on save. Warnings are surfaced but never hard-fail.
 *
 * `spawn` is injectable so tests can assert the argv without launching a real
 * editor; production passes the default `spawnSync`.
 */
export function runEdit(
  arg: string,
  opts: { projectDir: string },
  spawn: SpawnLike = spawnSync as unknown as SpawnLike,
): void {
  const projectDir = path.resolve(opts.projectDir);
  const { slug, manifestFile: target } = resolveSourceArg(arg, projectDir);

  if (!existsSync(target)) {
    logErr(
      `No curated manifest for "${slug}" at ${target}. ` +
        "Run `skaile source manifest init` first.",
    );
    process.exit(1);
  }

  const { command, args } = resolveEditorCommand(process.env.VISUAL || process.env.EDITOR, target);
  const res = spawn(command, args, { stdio: "inherit" });
  if (res.status !== 0 && res.status !== null) {
    logErr(`Editor exited with status ${res.status}.`);
    process.exit(1);
  }

  // Re-validate the saved file; surface diagnostics as warnings, don't hard-fail.
  const { diagnostics } = decodeSkaileManifest(readFileSync(target, "utf-8"));
  const errors = diagnostics.filter((d) => d.severity === "error");
  const warnings = diagnostics.filter((d) => d.severity !== "error");
  if (errors.length > 0) {
    for (const d of errors) logErr(`${d.code}: ${d.message}`);
    logWarn("Manifest has errors — fix them before `skaile source add` will consume it.");
  } else if (warnings.length > 0) {
    for (const d of warnings) logWarn(`${d.code}: ${d.message}`);
    logOk("Saved (with warnings).");
  } else {
    logOk("Saved.");
  }
}

/**
 * Append the `manifest` sub-tree to a `source` command.
 *
 * Called from `makeSourceCommand()` in `source.ts`.
 */
export function addSourceManifestCommands(src: Command): void {
  // enablePositionalOptions so options on the leaf verb (`source manifest init
  // --publisher …`) route to that verb instead of being rejected by `manifest`.
  const manifest = src
    .command("manifest")
    .description("Curated manifest lifecycle for a source")
    .enablePositionalOptions();

  manifest
    .command("init <src>")
    .description("Generate a curated manifest for a source (stored in the local store catalog)")
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .option("--publisher <name>", "Override the publisher (default: source's GitHub org)")
    .option("--dev", "Include assets under the source's dev_paths", false)
    .option("--force", "Overwrite an existing curated manifest", false)
    .action((arg, opts) => runInit(arg, opts));

  manifest
    .command("edit <src>")
    .description("Open the curated manifest in $EDITOR and re-validate on save")
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .action((arg, opts) => runEdit(arg, opts));

  manifest
    .command("rebuild <src>")
    .description(
      "Refresh a curated manifest after an upstream change (merge by default: " +
        "keeps curation, drops assets gone upstream, reports new ones without adding)",
    )
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .option("--publisher <name>", "Override the publisher (--overwrite only)")
    .option("--dev", "Include assets under the source's dev_paths", false)
    .option("--overwrite", "Discard curation and re-scan the full inventory", false)
    .action((arg, opts) => runRebuild(arg, opts));

  manifest
    .command("verify <src>")
    .description("Verify hashes, deps, and schema (read-only)")
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .option("--strict", "Fail on advisory checks (warnings force a nonzero exit)", false)
    .action((arg, opts) => runVerify(arg, opts));

  manifest
    .command("publish <src>")
    .description("Push the curated sidecar manifest and publish it to the store")
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .option("--as <namespace>", "Publisher namespace (default: your store username)")
    .option("--sidecar-repo <url>", "Public GitHub repo you own to hold the curated manifest")
    .option("--ref <gitref>", "Foreign source ref to ingest (branch/sha; default: main)")
    .option("--dry-run", "Print the publish plan without pushing or calling the store", false)
    .action(async (arg, opts) => {
      const { runPublish } = await import("./source-manifest-publish.ts");
      await runPublish(arg, opts);
    });

  manifest
    .command("propose <src>")
    .description("Open a PR to upstream with the sidecar manifest")
    .action(() => notWired("propose"));

  manifest
    .command("yank <src>")
    .description("Retract the source's published assets from the store")
    .option("-C, --project-dir <dir>", "Project directory", process.cwd())
    .option("--asset <ref>", "Yank only the asset matching this ref/name")
    .action(async (arg, opts) => {
      const { runYank } = await import("./source-manifest-publish.ts");
      await runYank(arg, opts);
    });
}
